/* eslint-disable */
import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, CircleAlert } from 'lucide-react';
import { DynamicQuestionForm } from '../index';
import type {
  GroupedQuestionnaireData,
  QuestionnaireMetadata,
  Answer,
} from '../../../types/api';
import { convertGroupedFieldsToQuestions } from '../../../utils/questionnaireUtils';
import {
  AccordionContainer,
  AccordionItem,
  AccordionHeader,
  AccordionTitle,
  AccordionIcon,
  AccordionContent,
} from './GroupedQuestionnaireForm.styled';
import {
  SectionDescription,
  SectionHeader,
  SectionTitle,
} from '../DynamicQuestionForm';
import styled from 'styled-components';
import { NoticeContainer } from '../FileUpload/FileUploadNotice';
import {
  useHotelQuestionnaireAnswers,
  useHotelQuestionnaireSteps,
} from '../../../hooks';
interface GroupedQuestionnaireFormProps {
  isReadonly?: boolean;
  data: GroupedQuestionnaireData;
  answers: Record<string, Answer>;
  onAnswerChange: (questionId: string, value: any, note?: string) => void;
}

const StyledText = styled.p<{ variant?: 'primary' | 'error' | 'default' }>`
  color: ${({ variant }) => {
    switch (variant) {
      case 'primary':
        return 'var(--color-primary-600)';
      case 'error':
        return 'var(--color-error)';
      default:
        return 'inherit';
    }
  }};

  @media (max-width: 768px) {
    font-size: var(--font-size-14);
  }
`;
const BoldSpan = styled.span`
  font-weight: bold;
`;

const GroupedQuestionnaireForm: React.FC<GroupedQuestionnaireFormProps> = ({
  isReadonly,
  data,
  answers,
  onAnswerChange,
}) => {
  const { combinedAnswers } = useHotelQuestionnaireAnswers();
  const { currentStepKey } = useHotelQuestionnaireSteps();
  
  // Track if country or destination has changed from initial values
  const hasCountryChangedRef = React.useRef(false);
  const hasDestinationChangedRef = React.useRef(false);
  
  const [openGroups, setOpenGroups] = useState<Set<string>>(() => {
    if (data.groups.length > 0) {
      return new Set([data.groups[0].key]);
    }
    return new Set();
  });

  // Set the first group as open by default when data loads
  useEffect(() => {
    if (data.groups.length > 0) {
      setOpenGroups(new Set([data.groups[0].key]));
    }
  }, []);

  const getQuestionCount = (fields: any) => {
    const totalQuestions = fields.filter(
      (field: any) => !field.excluded
    ).length;
    const answeredQuestions = fields.filter(
      (field: any) =>
        !field.excluded &&
        combinedAnswers[field.id] !== undefined &&
        !(
          combinedAnswers[field.id]?.value.selectedOptionId === null &&
          (combinedAnswers[field.id]?.value.typedValue === '' ||
            combinedAnswers[field.id]?.value.typedValue === null)
        )
    ).length;
    return { totalQuestions, answeredQuestions };
  };

  const handleGroupToggle = (groupKey: string) => {
    setOpenGroups(prev => {
      const newOpenGroups = new Set(prev);
      if (newOpenGroups.has(groupKey)) {
        newOpenGroups.delete(groupKey);
      } else {
        newOpenGroups.add(groupKey);
      }
      return newOpenGroups;
    });
  };

  // If there's only one group, render the form directly without accordion
  if (data.groups.length === 1) {
    const singleGroup = data.groups[0];
    const groupMetadata: QuestionnaireMetadata = {
      title: singleGroup.metadata.title,
      fields: convertGroupedFieldsToQuestions(singleGroup.metadata.fields),
    };
    const questionCount = getQuestionCount(groupMetadata.fields);

    return (
      <>
        {' '}
        {!isReadonly && (
          <SectionHeader className="!mb-0">
            <SectionTitle $isGrouped={false}>
              {groupMetadata.title.replaceAll('+', '&')}{' '}
              <span className="text-gray-500 sm:text-base text-sm sm:ml-4 ml-2">
                {questionCount.answeredQuestions}/{questionCount.totalQuestions}{' '}
                Answered
              </span>
            </SectionTitle>
            <SectionDescription>
              {(singleGroup.metadata as any).description}
            </SectionDescription>
            {currentStepKey === 'certifications' && (
              <NoticeContainer className="!rounded-2xl sm:!p-4 !p-3 mt-4">
                <CircleAlert size={20} color="var(--color-primary-500)" />
                <StyledText variant="primary">
                  <BoldSpan>IMPORTANT </BoldSpan> - Please ensure your answers
                  to other sections of the questionnaire reflect the
                  achievements within these certifications and any subsequent
                  advancements made.
                </StyledText>
              </NoticeContainer>
            )}
          </SectionHeader>
        )}
        <DynamicQuestionForm
          isGrouped={true}
          metadata={groupMetadata}
          answers={answers}
          onAnswerChange={onAnswerChange}
          hasCountryChangedRef={hasCountryChangedRef}
          hasDestinationChangedRef={hasDestinationChangedRef}
        />
      </>
    );
  }

  return (
    <AccordionContainer>
      {data.groups.map(group => {
        const isOpen = openGroups.has(group.key);

        // Convert grouped fields to questionnaire metadata format
        const groupMetadata: QuestionnaireMetadata = {
          title: group.metadata.title,
          fields: convertGroupedFieldsToQuestions(group.metadata.fields),
        };
        const questionCount = getQuestionCount(groupMetadata.fields);

        return (
          <AccordionItem key={group.key} $isOpen={isOpen}>
            <AccordionHeader
              $isOpen={isOpen}
              onClick={() => handleGroupToggle(group.key)}
            >
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <AccordionTitle>
                  {group.metadata.title.replaceAll('+', '&')}
                  <span className="text-gray-500 sm:text-base text-sm sm:ml-4 ml-2">
                    {questionCount.answeredQuestions}/
                    {questionCount.totalQuestions} Answered
                  </span>
                </AccordionTitle>
              </div>
              <AccordionIcon $isOpen={isOpen}>
                {isOpen ? (
                  <ChevronDown size={20} color="var(--color-gray-600)" />
                ) : (
                  <ChevronRight size={20} color="var(--color-gray-600)" />
                )}
              </AccordionIcon>
            </AccordionHeader>

            <AccordionContent $isOpen={isOpen}>
              {isOpen && (
                <>
                  {/* <AccordionNote>
                    <CircleAlert size={20} color="var(--color-primary-500)" />
                    <h1>
                      Use <span>"Skip"</span> if you're unsure about a
                      particular initiative
                    </h1>
                  </AccordionNote> */}
                  <DynamicQuestionForm
                    isGrouped={true}
                    metadata={groupMetadata}
                    answers={answers}
                    onAnswerChange={onAnswerChange}
                    hasCountryChangedRef={hasCountryChangedRef}
                    hasDestinationChangedRef={hasDestinationChangedRef}
                  />
                </>
              )}
            </AccordionContent>
          </AccordionItem>
        );
      })}
    </AccordionContainer>
  );
};

export default GroupedQuestionnaireForm;
